
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { rateLimiter, RATE_LIMITS, createRateLimitKey } from "@/utils/rateLimiting";
import { globalRealtimeManager } from "@/services/globalRealtimeManager";
import { useUnreadMessagesContext } from "@/contexts/UnreadMessagesContext";

interface Message {
  id: string;
  content: string;
  sender_id: string;
  receiver_id: string;
  created_at: string;
  is_read: boolean;
  message_type: string | null;
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  duration: number | null;
  thumbnail_url: string | null;
  location_data: any;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  isPending?: boolean;
  isError?: boolean;
  retryCount?: number;
}

interface ChatMetrics {
  totalMessages: number;
  unreadCount: number;
  lastActivity: string | null;
}

export const useChatEnhanced = (matchId: string | undefined) => {
  const { user } = useAuth();
  const { decrementUnreadCount } = useUnreadMessagesContext();
  const [messages, setMessages] = useState<Message[]>([]);
  const [metrics, setMetrics] = useState<ChatMetrics>({
    totalMessages: 0,
    unreadCount: 0,
    lastActivity: null
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [partnerOnline, setPartnerOnline] = useState(false);
  
  const unsubscribeRefs = useRef<(() => void)[]>([]);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const currentMatchIdRef = useRef<string | undefined>(matchId);

  const parseMatchId = (matchId: string): string[] => {
    if (matchId.length < 73) return [];
    
    const firstUuidEnd = 36;
    const user1Id = matchId.substring(0, firstUuidEnd);
    const user2Id = matchId.substring(firstUuidEnd + 1);
    
    return [user1Id, user2Id];
  };

  const createChannelNames = (userId1: string, userId2: string) => {
    const sortedIds = [userId1, userId2].sort();
    return {
      messages: `chat-messages-${sortedIds[0]}-${sortedIds[1]}`,
      typing: `typing-${sortedIds[0]}-${sortedIds[1]}`,
      presence: `presence-${sortedIds[0]}-${sortedIds[1]}`
    };
  };

  const updateMessageStatus = useCallback((messageId: string, status: Message['status']) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, status, isPending: status === 'sending', isError: status === 'failed' } : msg
    ));
  }, []);

  const sendMessage = useCallback(async (content: string, additionalData?: Partial<Message>) => {
    if (!user || !matchId || !content.trim() || sending) return false;

    // Rate limiting check
    const rateLimitKey = createRateLimitKey(user.id, 'MESSAGE_SEND');
    if (!rateLimiter.isAllowed(rateLimitKey, RATE_LIMITS.MESSAGE_SEND)) {
      const remainingTime = rateLimiter.getRemainingTime(rateLimitKey, RATE_LIMITS.MESSAGE_SEND);
      toast({
        title: "Rate Limit Exceeded",
        description: `Please wait ${Math.ceil(remainingTime / 1000)} seconds before sending another message.`,
        variant: "destructive",
      });
      return false;
    }

    const userIds = parseMatchId(matchId);
    const otherUserId = userIds.find(id => id !== user.id);
    
    if (!otherUserId) return false;

    // Check if user is blocked before sending message
    try {
      const { useBlockUser } = await import('@/hooks/useBlockUser');
      const { checkIfBlocked } = useBlockUser();
      
      const [isUserBlocked, isPartnerBlocked] = await Promise.all([
        checkIfBlocked(otherUserId), // Check if current user blocked the recipient
        supabase.from('user_blocks')
          .select('id')
          .eq('blocker_id', otherUserId)
          .eq('blocked_id', user.id)
          .maybeSingle()
          .then(({ data }) => !!data) // Check if recipient blocked current user
      ]);

      if (isUserBlocked) {
        toast({
          title: "Cannot Send Message",
          description: "You have blocked this user.",
          variant: "destructive",
        });
        return false;
      }

      if (isPartnerBlocked) {
        toast({
          title: "Cannot Send Message", 
          description: "This user has blocked you.",
          variant: "destructive",
        });
        return false;
      }
    } catch (blockCheckError) {
      console.warn('Error checking block status:', blockCheckError);
      // Continue with message sending if block check fails
    }

    setSending(true);
    
    // Create optimistic message with additional data
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const optimisticMessage: Message = {
      id: tempId,
      content: content.trim(),
      sender_id: user.id,
      receiver_id: otherUserId,
      created_at: new Date().toISOString(),
      is_read: false,
      message_type: additionalData?.message_type || 'text',
      file_url: additionalData?.file_url || null,
      file_name: additionalData?.file_name || null,
      file_type: additionalData?.file_type || null,
      file_size: additionalData?.file_size || null,
      duration: additionalData?.duration || null,
      thumbnail_url: additionalData?.thumbnail_url || null,
      location_data: additionalData?.location_data || null,
      status: 'sending',
      isPending: true
    };

    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const messageData = {
        sender_id: user.id,
        receiver_id: otherUserId,
        content: content.trim(),
        message_type: additionalData?.message_type || 'text',
        file_url: additionalData?.file_url || null,
        file_name: additionalData?.file_name || null,
        file_type: additionalData?.file_type || null,
        file_size: additionalData?.file_size || null,
        duration: additionalData?.duration || null,
        thumbnail_url: additionalData?.thumbnail_url || null,
        location_data: additionalData?.location_data || null
      };

      const { data, error } = await supabase
        .from('messages')
        .insert(messageData)
        .select()
        .single();

      if (error) throw error;

      // Replace optimistic message with real message
      setMessages(prev => prev.map(msg => 
        msg.id === tempId ? { ...data, status: 'sent' } : msg
      ));

      // Update metrics
      setMetrics(prev => ({
        ...prev,
        totalMessages: prev.totalMessages + 1,
        lastActivity: new Date().toISOString()
      }));

      // Send push notification after successful message save (but check blocking first)
      try {
        // Check if recipient has blocked sender before sending notification
        const { data: isBlocked } = await supabase
          .from('user_blocks')
          .select('id')
          .eq('blocker_id', otherUserId)
          .eq('blocked_id', user.id)
          .maybeSingle();

        if (!isBlocked) {
          // Get sender's display name for the notification
          const { data: senderProfile } = await supabase
            .from('user_profiles')
            .select('display_name, first_name')
            .eq('user_id', user.id)
            .single();

          const senderName = senderProfile?.display_name || senderProfile?.first_name || 'Someone';
          
          // Import and call notification service
          const { notificationService } = await import('@/services/notificationService');
          await notificationService.sendMessageNotification(otherUserId, senderName, matchId);
        }
      } catch (notificationError) {
        // Don't fail the message if notification fails
        console.warn('Failed to send push notification:', notificationError);
      }

      return true;

    } catch (error: any) {
      console.error('Error sending message:', error);
      updateMessageStatus(tempId, 'failed');
      
      toast({
        title: "Message Failed",
        description: "Your message could not be sent. Please try again.",
        variant: "destructive",
      });
      
      return false;
    } finally {
      setSending(false);
    }
  }, [user, matchId, sending, updateMessageStatus]);

  const retryMessage = useCallback(async (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !user || !matchId) return;

    const userIds = parseMatchId(matchId);
    const otherUserId = userIds.find(id => id !== user.id);
    if (!otherUserId) return;

    updateMessageStatus(messageId, 'sending');

    try {
      const messageData = {
        sender_id: user.id,
        receiver_id: otherUserId,
        content: message.content,
        message_type: 'text'
      };

      const { data, error } = await supabase
        .from('messages')
        .insert(messageData)
        .select()
        .single();

      if (error) throw error;

      // Replace failed message with new message
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...data, status: 'sent' } : msg
      ));

    } catch (error: any) {
      console.error('Error retrying message:', error);
      updateMessageStatus(messageId, 'failed');
      
      toast({
        title: "Retry Failed",
        description: "Message could not be sent. Please check your connection.",
        variant: "destructive",
      });
    }
  }, [messages, user, matchId, updateMessageStatus]);

  const markMessagesAsRead = useCallback(async () => {
    if (!user || !matchId) return;

    const userIds = parseMatchId(matchId);
    const otherUserId = userIds.find(id => id !== user.id);
    if (!otherUserId) return;

    // Count unread messages before marking them as read
    const unreadMessages = messages.filter(msg => 
      msg.sender_id === otherUserId && 
      msg.receiver_id === user.id && 
      !msg.is_read
    );
    const unreadCount = unreadMessages.length;

    // Skip if no unread messages to avoid duplicate calls
    if (unreadCount === 0) {
      console.log(`📖 [ChatEnhanced] No unread messages to mark as read`);
      return;
    }

    console.log(`📖 [ChatEnhanced] Marking ${unreadCount} messages as read in chat`);

    try {
      const { error } = await supabase
        .from('messages')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('sender_id', otherUserId)
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.sender_id === otherUserId && msg.receiver_id === user.id 
          ? { ...msg, is_read: true } 
          : msg
      ));

      setMetrics(prev => ({ ...prev, unreadCount: 0 }));

      // Wait a moment for database propagation, then update global count
      setTimeout(() => {
        decrementUnreadCount(unreadCount);
      }, 100); // Small delay to ensure DB write is complete

    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [user, matchId, messages, decrementUnreadCount]);

  const updateTypingStatus = useCallback(async (typing: boolean) => {
    if (!user || !matchId) return;

    const userIds = parseMatchId(matchId);
    const otherUserId = userIds.find(id => id !== user.id);
    if (!otherUserId) return;

    try {
      await supabase
        .from('typing_status')
        .upsert({
          user_id: user.id,
          chat_partner_id: otherUserId,
          is_typing: typing,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,chat_partner_id',
          ignoreDuplicates: false
        });
    } catch (error) {
      console.error('Error updating typing status:', error);
    }
  }, [user, matchId]);

  const handleTyping = useCallback(() => {
    updateTypingStatus(true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      updateTypingStatus(false);
    }, 2000);
  }, [updateTypingStatus]);

  const loadMessages = useCallback(async (retryCount = 0) => {
    if (!user || !matchId) return;

    // Prevent multiple simultaneous requests
    if (loading && retryCount === 0) {
      console.log(`⏸️ [ChatEnhanced] Skipping loadMessages - already loading`);
      return;
    }

    // Emergency timeout - force loading to false after 15 seconds
    const emergencyTimeout = setTimeout(() => {
      console.warn('🚨 [ChatEnhanced] Emergency timeout - forcing loading to false');
      setLoading(false);
    }, 15000);

    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s

    try {
      const userIds = parseMatchId(matchId);
      const otherUserId = userIds.find(id => id !== user.id);
      if (!otherUserId) return;

      console.log(`📚 [ChatEnhanced] Loading messages (attempt ${retryCount + 1}/${maxRetries + 1})`);

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const { data: messagesData, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${user.id})`)
        .order('created_at', { ascending: true })
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) throw error;

      const messagesWithStatus = (messagesData || []).map(msg => ({
        ...msg,
        status: msg.is_read ? 'read' as const : 'delivered' as const
      }));

      setMessages(messagesWithStatus);

      // Calculate metrics
      const unreadCount = messagesData?.filter(msg => 
        msg.sender_id === otherUserId && msg.receiver_id === user.id && !msg.is_read
      ).length || 0;

      const lastMessage = messagesData?.[messagesData.length - 1];

      setMetrics({
        totalMessages: messagesData?.length || 0,
        unreadCount,
        lastActivity: lastMessage?.created_at || null
      });

      // Auto-mark messages as read when user opens chat
      if (unreadCount > 0) {
        setTimeout(() => markMessagesAsRead(), 500); // Small delay to avoid race conditions
      }

      console.log(`✅ [ChatEnhanced] Successfully loaded ${messagesData?.length || 0} messages`);

    } catch (error: any) {
      console.error(`❌ [ChatEnhanced] Error loading messages (attempt ${retryCount + 1}):`, error);
      
      // Check if it's a network error and we should retry
      const isNetworkError = error.name === 'AbortError' || 
                            error.message?.includes('fetch') || 
                            error.message?.includes('network') ||
                            error.code === 'PGRST116'; // Supabase timeout

      if (isNetworkError && retryCount < maxRetries) {
        console.log(`🔄 [ChatEnhanced] Retrying in ${retryDelay}ms...`);
        setTimeout(() => {
          loadMessages(retryCount + 1);
        }, retryDelay);
        return;
      }

      // Final failure - show error to user
      toast({
        title: "Connection Error",
        description: retryCount >= maxRetries 
          ? "Failed to load messages after multiple attempts. Please check your connection."
          : "Could not load conversation history. Please try again.",
        variant: "destructive",
      });
    } finally {
      clearTimeout(emergencyTimeout); // Clear emergency timeout
      if (retryCount === 0) { // Only set loading false on the original call
        setLoading(false);
      }
    }
  }, [user, matchId, loading, markMessagesAsRead]);

  const setupRealtimeConnections = useCallback(() => {
    if (!user || !matchId) return;

    const userIds = parseMatchId(matchId);
    const otherUserId = userIds.find(id => id !== user.id);
    if (!otherUserId) return;

    setConnectionStatus('reconnecting');

    // Cleanup existing subscriptions
    unsubscribeRefs.current.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        console.warn('Error unsubscribing:', error);
      }
    });
    unsubscribeRefs.current = [];

    try {
      // Use Global Realtime Manager for conversations (which handles messages)
      const conversationUnsubscribe = globalRealtimeManager.subscribeToConversations(user.id, (payload) => {
        if (payload && payload.eventType === 'INSERT') {
          const newMessage = payload.new as Message;
          // Only handle messages for this specific conversation
          if ((newMessage.sender_id === user.id && newMessage.receiver_id === otherUserId) ||
              (newMessage.sender_id === otherUserId && newMessage.receiver_id === user.id)) {
            
            if (newMessage.sender_id !== user.id) {
              setMessages(prev => [...prev, { ...newMessage, status: 'delivered' }]);
              setMetrics(prev => ({ 
                ...prev, 
                totalMessages: prev.totalMessages + 1,
                unreadCount: prev.unreadCount + 1,
                lastActivity: newMessage.created_at
              }));
            }
          }
        } else if (payload && payload.eventType === 'UPDATE') {
          const updatedMessage = payload.new as Message;
          if ((updatedMessage.sender_id === user.id && updatedMessage.receiver_id === otherUserId) ||
              (updatedMessage.sender_id === otherUserId && updatedMessage.receiver_id === user.id)) {
            setMessages(prev => prev.map(msg => 
              msg.id === updatedMessage.id 
                ? { ...msg, ...updatedMessage, status: updatedMessage.is_read ? 'read' : 'delivered' }
                : msg
            ));
          }
        }
      });

      unsubscribeRefs.current.push(conversationUnsubscribe);
      setConnectionStatus('connected');

    } catch (error) {
      console.error('Error setting up realtime connections:', error);
      setConnectionStatus('disconnected');
    }
  }, [user, matchId]);

  useEffect(() => {
    currentMatchIdRef.current = matchId;

    if (user && matchId) {
      loadMessages();
      setupRealtimeConnections();
    }
    
    return () => {
      // Cleanup all subscriptions on unmount or dependency change
      unsubscribeRefs.current.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn('Error cleaning up subscription:', error);
        }
      });
      unsubscribeRefs.current = [];
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [user, matchId, loadMessages, setupRealtimeConnections]);

  return {
    messages,
    metrics,
    loading,
    sending,
    connectionStatus,
    partnerTyping,
    partnerOnline,
    sendMessage,
    retryMessage,
    handleTyping,
    markMessagesAsRead,
    updateTypingStatus
  };
};
